import React, { useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import ProductMockWindow from "@/components/ProductMockWindow";
import useAutoCycle from "@/hooks/useAutoCycle";

function NeedsMock() {
  const fields = [
    { label: "Cancer type", value: "Triple-negative breast" },
    { label: "Location", value: "Houston, Texas" },
    { label: "Insurance", value: "UnitedHealthcare" },
    { label: "Urgency", value: "Staging this week" },
  ];
  return (
    <ProductMockWindow url="getbyonco.com/get-matched" footer={<span>A few questions · then a match</span>}>
      <div className="eh-window-kicker">Intake</div>
      <div className="eh-window-title">Tell us your needs</div>
      <div className="eh-row-list">
        {fields.map((f, i) => (
          <motion.div
            key={f.label}
            className="eh-row"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 * i }}
          >
            <span className="eh-row-meta">{f.label}</span>
            <strong>{f.value}</strong>
          </motion.div>
        ))}
      </div>
    </ProductMockWindow>
  );
}

function MatchMock() {
  const rows = [
    { name: "Memorial Sloan Kettering Cancer Center", meta: "New York City · NCI comprehensive", status: "Best fit" },
    { name: "MD Anderson Cancer Center", meta: "Houston · NCI comprehensive", status: "Matched" },
    { name: "Mayo Clinic Cancer Center", meta: "Rochester, MN · NCI comprehensive", status: "Matched" },
  ];
  return (
    <ProductMockWindow
      url="getbyonco.com/find-hospitals"
      footer={
        <>
          <span>Filtered from verified US profiles</span>
          <span className="eh-pill eh-pill--ok">Matches ready</span>
        </>
      }
    >
      <div className="eh-window-head">
        <div>
          <div className="eh-window-kicker">Matched centers</div>
          <div className="eh-window-title">Your care plan</div>
        </div>
        <div className="eh-window-metrics">
          <span>NCI first</span>
          <span>3 centers</span>
        </div>
      </div>
      <div className="eh-row-list">
        {rows.map((row, i) => (
          <motion.div
            key={row.name}
            className="eh-connect-row"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 * i }}
          >
            <span className="eh-connect-icon" />
            <div>
              <strong>{row.name}</strong>
              <span>{row.meta}</span>
            </div>
            <span className="eh-pill eh-pill--ok">{row.status}</span>
          </motion.div>
        ))}
      </div>
    </ProductMockWindow>
  );
}

function TrackMock() {
  return (
    <ProductMockWindow
      url="getbyonco.com/journey-builder"
      footer={<span>Appointments, reports, progress. One dashboard.</span>}
    >
      <div className="eh-window-kicker">Care dashboard</div>
      <div className="eh-window-title">Book &amp; track</div>
      <div className="eh-review-card">
        <div className="eh-review-head">
          <strong>This week</strong>
          <span>3 visits · 2 reports</span>
        </div>
        <motion.div className="eh-row" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
          <span className="eh-dot eh-dot--ok" />
          <span>PET-CT uploaded</span>
          <span className="eh-check">In record</span>
        </motion.div>
        <motion.div className="eh-row" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <span className="eh-dot eh-dot--info" />
          <span>Oncology consult</span>
          <span className="eh-check">Tue 10:30 AM ET</span>
        </motion.div>
      </div>
    </ProductMockWindow>
  );
}

function HelpMock() {
  const rows = [
    { title: "Prior authorization", meta: "UnitedHealthcare · from our USA insurer list" },
    { title: "Second opinion", meta: "Memorial Sloan Kettering / MD Anderson" },
    { title: "Care coordinator", meta: "Phone · US hours" },
  ];
  return (
    <ProductMockWindow url="getbyonco.com/app" footer={<span>Help when you need it, anytime</span>}>
      <div className="eh-window-kicker">Care support</div>
      <div className="eh-window-title">Get help when you need</div>
      <div className="eh-row-list">
        {rows.map((row, i) => (
          <motion.div
            key={row.title}
            className="eh-connect-row"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 * i }}
          >
            <span className="eh-connect-icon" />
            <div>
              <strong>{row.title}</strong>
              <span>{row.meta}</span>
            </div>
            <span className="eh-check">Connected</span>
          </motion.div>
        ))}
      </div>
    </ProductMockWindow>
  );
}

const MOCKS = [NeedsMock, MatchMock, TrackMock, HelpMock];

export default function HowItWorksStepper({ steps, eyebrow = "How it works", heading, lead }) {
  const [paused, setPaused] = useState(false);
  const rootRef = React.useRef(null);
  const inView = useInView(rootRef, { amount: 0.25 });
  const { index, setIndex, progress } = useAutoCycle(steps.length, 5200, paused || !inView);
  const Mock = MOCKS[index] || NeedsMock;

  return (
    <div
      ref={rootRef}
      className="eh-stepper"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="eh-stepper-visual">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <Mock />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="eh-stepper-copy">
        <p className="eh-eyebrow">{eyebrow}</p>
        <h2 className="eh-showcase-title">{heading}</h2>
        {lead ? <p className="eh-showcase-desc" style={{ marginBottom: 8 }}>{lead}</p> : null}
        <ol className="eh-steps">
          {steps.map((step, i) => {
            const active = i === index;
            return (
              <li key={step.title} className={active ? "is-active" : ""}>
                <button type="button" onClick={() => setIndex(i)}>
                  <span className="eh-step-rail" aria-hidden="true" />
                  <span className="eh-step-title">{step.title}</span>
                  <AnimatePresence initial={false}>
                    {active && (
                      <motion.div
                        className="eh-step-desc"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      >
                        {step.desc}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {active && (
                    <span className="eh-step-progress" aria-hidden="true">
                      <motion.span style={{ scaleX: progress }} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
